// Authoring-ready ES-module source for each gallery kernel — the "Fork this kernel" starting
// points (utility-redesign plan §5). The gallery kernels themselves are TypeScript objects in
// src/kernels; these are hand-ported plain-JS twins that (a) pass the determinism linter,
// (b) reproduce the same result bits as their gallery original for the same (seed, N, params),
// and (c) read as a clean example an author can edit. The fidelity is guarded by
// test/fork-sources.test.ts (loads each source and asserts bit-equality with the gallery kernel).
//
// Rules every source here obeys (and that authors must keep): randomness ONLY via `rng`;
// transcendentals ONLY via the `math` shim; reduce in the given (fixed) order.

export const PI_SOURCE = `// π by sampling — arithmetic + rng only, so it reproduces bitwise across engines
// (verifiable: true) with no math shim.
export default {
  id: "pi",
  label: "π by sampling",
  verifiable: true,
  resultUnit: "π estimate",
  chunkSize: 25000,

  chunk(count, rng) {
    let hits = 0;
    for (let i = 0; i < count; i++) {
      const x = rng.float();
      const y = rng.float();
      if (x * x + y * y < 1) hits++;
    }
    return { hits, count };
  },

  reduce(orderedPartials) {
    let hits = 0, count = 0;
    const samples = [];
    for (const p of orderedPartials) {
      hits += p.hits;
      count += p.count;
      samples.push(p.count > 0 ? (4 * p.hits) / p.count : 0);
    }
    return { result: count > 0 ? (4 * hits) / count : 0, samples };
  },
};
`;

export const SIR_SOURCE = `// SIR epidemic — stochastic compartment model. Arithmetic + rng only (binomial-by-counting),
// so verifiable: true without the math shim. Each iteration runs one full outbreak.
export default {
  id: "sir",
  label: "SIR epidemic (stochastic)",
  verifiable: true,
  resultUnit: "mean attack rate",
  chunkSize: 40,

  params: [
    { key: "pop", label: "Population", default: 200, min: 20, max: 2000, step: 10,
      hint: "Individuals per stochastic outbreak. Larger = heavier per-trial work." },
    { key: "beta", label: "Transmission (β)", default: 0.35, min: 0.01, max: 2, step: 0.01,
      hint: "Transmission rate per infectious contact. β/γ is R₀." },
    { key: "gamma", label: "Recovery (γ)", default: 0.1, min: 0.01, max: 1, step: 0.01,
      hint: "Recovery rate per step (1 / infectious period)." },
  ],

  chunk(count, rng, math, params) {
    const POP = params.pop, BETA = params.beta, GAMMA = params.gamma;
    const STEPS = 120;
    let attackSum = 0, peakSum = 0;
    for (let t = 0; t < count; t++) {
      let s = POP - 1, i = 1, r = 0, peak = i;
      for (let step = 0; step < STEPS && i > 0; step++) {
        const pInf = (BETA * i) / POP;
        let newInf = 0;
        for (let k = 0; k < s; k++) if (rng.float() < pInf) newInf++;
        let newRec = 0;
        for (let k = 0; k < i; k++) if (rng.float() < GAMMA) newRec++;
        s -= newInf;
        i += newInf - newRec;
        r += newRec;
        if (i > peak) peak = i;
      }
      attackSum += (POP - s) / POP;
      peakSum += peak / POP;
    }
    return { attackSum, peakSum, trials: count };
  },

  reduce(orderedPartials) {
    let attackSum = 0, peakSum = 0, trials = 0;
    const samples = [];
    for (const p of orderedPartials) {
      attackSum += p.attackSum;
      peakSum += p.peakSum;
      trials += p.trials;
      samples.push(p.trials > 0 ? p.attackSum / p.trials : 0);
    }
    return { result: trials > 0 ? attackSum / trials : 0, samples };
  },
};
`;

export const OPTIONS_SOURCE = `// Options pricing (GBM) — European call by Monte Carlo. Box-Muller pulls log/cos/exp through
// the \`math\` shim (fdlibm), so it reproduces bitwise across engines (verifiable: true).
export default {
  id: "options",
  label: "Options pricing (GBM)",
  verifiable: true,
  resultUnit: "call price",
  chunkSize: 10000,

  params: [
    { key: "s0", label: "Spot price", default: 100, min: 1, max: 1000, step: 1, unit: "$",
      hint: "Current price of the underlying (S₀)." },
    { key: "k", label: "Strike", default: 105, min: 1, max: 1000, step: 1, unit: "$",
      hint: "Exercise price of the call (K)." },
    { key: "r", label: "Risk-free rate", default: 3, min: -5, max: 25, step: 0.1, unit: "%",
      hint: "Annualised risk-free interest rate." },
    { key: "sigma", label: "Volatility", default: 20, min: 1, max: 150, step: 0.5, unit: "%",
      hint: "Annualised volatility of the underlying (σ)." },
    { key: "t", label: "Time to expiry", default: 1, min: 0.05, max: 10, step: 0.05, unit: "yr",
      hint: "Years until the option expires (T)." },
  ],

  chunk(count, rng, math, params) {
    const TWO_PI = 6.283185307179586;
    const S0 = params.s0, K = params.k;
    const R = params.r / 100, SIGMA = params.sigma / 100, T = params.t;
    const drift = (R - (SIGMA * SIGMA) / 2) * T;
    const vol = SIGMA * math.sqrt(T);
    const discount = math.exp(-R * T);
    let payoffSum = 0;
    for (let p = 0; p < count; p++) {
      const u1 = rng.float();
      const u2 = rng.float();
      const z = math.sqrt(-2 * math.log(u1 > 0 ? u1 : 1e-12)) * math.cos(TWO_PI * u2);
      const sT = S0 * math.exp(drift + vol * z);
      const payoff = sT > K ? sT - K : 0;
      payoffSum += discount * payoff;
    }
    return { payoffSum, paths: count };
  },

  reduce(orderedPartials) {
    let payoffSum = 0, paths = 0;
    const samples = [];
    for (const p of orderedPartials) {
      payoffSum += p.payoffSum;
      paths += p.paths;
      samples.push(p.paths > 0 ? p.payoffSum / p.paths : 0);
    }
    return { result: paths > 0 ? payoffSum / paths : 0, samples };
  },
};
`;

export const AGRITECH_SOURCE = `// Agritech yield sweep — per-microclimate crop yield under weather uncertainty. Lognormal
// rainfall + Box-Muller temperature shock use log/cos/exp through the \`math\` shim (fdlibm),
// so it reproduces bitwise across engines (verifiable: true).
export default {
  id: "agritech",
  label: "Agritech yield sweep",
  verifiable: true,
  resultUnit: "t/ha (mean yield)",
  chunkSize: 10000,

  params: [
    { key: "baseYield", label: "Potential yield", default: 9.5, min: 1, max: 30, step: 0.5, unit: "t/ha",
      hint: "Yield under ideal conditions, before weather penalties." },
    { key: "rainOpt", label: "Optimal rainfall", default: 650, min: 100, max: 2000, step: 10, unit: "mm",
      hint: "Rainfall at which the yield response peaks." },
    { key: "gddOpt", label: "Optimal heat (GDD)", default: 1500, min: 500, max: 4000, step: 50,
      hint: "Growing-degree-days at which the heat response peaks." },
    { key: "failThreshold", label: "Failure threshold", default: 3, min: 0, max: 10, step: 0.25, unit: "t/ha",
      hint: "A season below this yield counts as a crop failure." },
  ],

  chunk(count, rng, math, params) {
    const TWO_PI = 6.283185307179586;
    const RAIN_WIDTH = 260, GDD_WIDTH = 600;
    const BASE_YIELD = params.baseYield, RAIN_OPT = params.rainOpt;
    const GDD_OPT = params.gddOpt, FAILURE_THRESHOLD = params.failThreshold;
    let yieldSum = 0, failures = 0;
    for (let s = 0; s < count; s++) {
      const u1 = rng.float();
      const u2 = rng.float();
      const zRain = math.sqrt(-2 * math.log(u1 > 0 ? u1 : 1e-12)) * math.cos(TWO_PI * u2);
      const u3 = rng.float();
      const u4 = rng.float();
      const zGdd = math.sqrt(-2 * math.log(u3 > 0 ? u3 : 1e-12)) * math.cos(TWO_PI * u4);

      const rainfall = RAIN_OPT * math.exp(0.18 * zRain);
      const gdd = GDD_OPT + 320 * zGdd;

      // Gaussian response in [0,1]: 1 at the optimum, falling off with (x−opt)²/width².
      const dRain = (rainfall - RAIN_OPT) / RAIN_WIDTH;
      const respRain = math.exp(-(dRain * dRain));
      const dGdd = (gdd - GDD_OPT) / GDD_WIDTH;
      const respGdd = math.exp(-(dGdd * dGdd));

      const shock = 0.9 + 0.2 * rng.float();
      const y = BASE_YIELD * respRain * respGdd * shock;

      yieldSum += y;
      if (y < FAILURE_THRESHOLD) failures++;
    }
    return { yieldSum, failures, seasons: count };
  },

  reduce(orderedPartials) {
    let yieldSum = 0, seasons = 0;
    const samples = [];
    for (const p of orderedPartials) {
      yieldSum += p.yieldSum;
      seasons += p.seasons;
      samples.push(p.seasons > 0 ? p.yieldSum / p.seasons : 0);
    }
    return { result: seasons > 0 ? yieldSum / seasons : 0, samples };
  },
};
`;

/** Fork source for a gallery kernel id, or null if none exists. */
export const FORK_SOURCES: Record<string, string> = {
  pi: PI_SOURCE,
  sir: SIR_SOURCE,
  options: OPTIONS_SOURCE,
  agritech: AGRITECH_SOURCE,
};

export function forkSource(kernelId: string): string | null {
  return FORK_SOURCES[kernelId] ?? null;
}
